import { Suspense, useEffect } from 'react';
import { I18nextProvider } from 'react-i18next';
import { Provider as ReduxProvider } from 'react-redux';
import { Route, BrowserRouter as Router, Routes, useLocation, useNavigate } from 'react-router';

import i18n from '../i18n';

import { LineUpsTab } from './Match/LineUpsTab';
import { MatchOfficialsTab } from './Match/MatchOfficialsTab';
import { MatchOverviewTab } from './Match/MatchOverviewTab';
import { PrintMatchPressKitsTab } from './Match/PrintMatchPressKitsTab';
import { SquadDetailsTab } from './Match/SquadDetailsTab';
import { StandingsTab } from './Match/StandingsTab';
import { StatisticsTab } from './Match/StatisticsTab';

import Spinner from 'components/spinner';
import { routes } from 'routes';
import Competition from 'routes/competition';
import Match from 'routes/match';
import NotFoundPage from 'routes/notfound';
import Redirect from 'routes/redirect';
import { publicPath } from 'signals/publicPath';
import store from 'store/configureStore';

const menu = document.querySelector<HTMLElement>('.article-header-menu .menu-item-wrapper');
const menuItems = menu?.querySelectorAll<HTMLLinkElement>('.js-menu-item') ?? [];

document.querySelectorAll<HTMLElement>('.presskit-hidden').forEach((item) => {
  item.style.removeProperty('display');
});

const UrlHandler = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    menuItems.forEach((item, index) => {
      if (index === 0) return;
      item.addEventListener('click', (e) => {
        e.preventDefault();

        const href = item.getAttribute('href');
        href && navigate(href);

        return false;
      });
    });
  }, [navigate]);

  const setMenuActive = (url: string) => {
    menuItems.forEach((item, index) => {
      const href = item.getAttribute('href');
      const active = index !== 0 && url.startsWith(href ?? '');

      item.classList.toggle('active', active);
      item.classList.toggle('pk-theme--light', active);
      const chip = item.querySelector('pk-chip');
      if (chip) chip.selected = active;

      if (!active) return;

      item.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    });
  };

  useEffect(() => {
    setMenuActive(location.pathname);

    const isMatchPage = location.pathname?.includes('/match/');
    document.body.classList.toggle('pr-match-page', isMatchPage);
    isMatchPage && window.scrollTo({ top: 0 });
  }, [location.pathname]);
  return null;
};

const App = () => {
  const PUBLIC_PATH = publicPath.value;

  return (
    <div id="app">
      <I18nextProvider i18n={i18n}>
        <Suspense fallback={null}>
          <ReduxProvider store={store}>
            <Suspense fallback={null}>
              <Spinner />
            </Suspense>
            <Router>
              <UrlHandler />
              <Routes>
                <Route path={PUBLIC_PATH} element={<Redirect />} />
                <Route path={`${PUBLIC_PATH}competitions/:competitionName?`} element={<Redirect />} />
                <Route path={`${PUBLIC_PATH}competitions/:competitionName/:seasonYear`} element={<Competition />} />
                <Route
                  path={`${PUBLIC_PATH}competitions/:competitionName/:seasonYear/match/:matchId/:subPage?`}
                  element={<Match />}
                >
                  <Route index element={<MatchOverviewTab />} />
                  <Route path={routes.squadDetails} element={<SquadDetailsTab />} />
                  <Route path={routes.officials} element={<MatchOfficialsTab />} />
                  <Route path={routes.lineUps} element={<LineUpsTab />} />
                  <Route path={routes.statistics} element={<StatisticsTab />} />
                  <Route path={routes.standings} element={<StandingsTab />} />
                  <Route path={routes.printMatchPressKits} element={<PrintMatchPressKitsTab />} />
                </Route>
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </Router>
          </ReduxProvider>
        </Suspense>
      </I18nextProvider>
    </div>
  );
};

export default App;
